// ============================================================
// ExpressMockingExternalAPIs.jsx
//
// A branded, responsive course content page for the
// "Mocking External APIs" lesson.
//
// Author: ProDev Linux Series (Testing & Quality Branch)
// Features: Jest mock patterns, service isolation, and
//           navigation bridge to the Dashboard.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import ScienceIcon            from "@mui/icons-material/Science";
import HubIcon               from "@mui/icons-material/Hub";
import PaymentsIcon          from "@mui/icons-material/Payments";
import EmailIcon             from "@mui/icons-material/Email";
import TerminalIcon          from "@mui/icons-material/Terminal";
import LightbulbIcon         from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon       from "@mui/icons-material/ContentCopy";
import DoneIcon              from "@mui/icons-material/Done";
import SchoolIcon            from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon         from "@mui/icons-material/ArrowBack";
import CodeIcon              from "@mui/icons-material/Code";
import BugReportIcon          from "@mui/icons-material/BugReport";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}>// {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressMockingExternalAPIs() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Quality Assurance · Module 26"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Mocking <span>External APIs</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        External services like Stripe or SendGrid should be mocked in tests to avoid network latency, cost, and unpredictable side effects. Mocking allows you to simulate real-world API behavior locally with total reliability.
                    </Typography>
                </Box>

                {/* 1. SERVICE ENCAPSULATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<HubIcon />} label="1. Encapsulating the Logic" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Before mocking, wrap external SDK calls into a dedicated service file. This makes it easier to swap or mock dependencies across your app.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "// services/paymentService.js", comment: "" },
                        { cmd: "const stripe = require('stripe')(process.env.STRIPE_SECRET);", comment: "" },
                        { cmd: "", comment: "" },
                        { cmd: "async function charge(amount, source) {", comment: "Isolated service function" },
                        { cmd: "  return stripe.charges.create({ amount, currency: 'usd', source });", comment: "" },
                        { cmd: "}", comment: "" },
                        { cmd: "", comment: "" },
                        { cmd: "module.exports = { charge };", comment: "" },
                    ]} />
                </Box>

                {/* 2. THE MOCK BLUEPRINT */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<BugReportIcon />} label="2. Creating the Mock" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Create a manual mock in a <code style={{color: BRAND.white}}>__mocks__</code> folder or inline within your test file. This intercepts the library's constructor.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "// __mocks__/stripe.js", comment: "" },
                        { cmd: "module.exports = () => ({", comment: "" },
                        { cmd: "  charges: {", comment: "" },
                        { cmd: "    create: jest.fn().mockResolvedValue({ id: 'ch_123', status: 'succeeded' })", comment: "Simulate API response" },
                        { cmd: "  }", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                {/* 3. TEST IMPLEMENTATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<ScienceIcon />} label="3. Running the Test" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Tell Jest to use your mock. When your service calls <code style={{color: BRAND.white}}>require('stripe')</code>, it will receive your fake implementation instead.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "jest.mock('stripe');", comment: "Trigger the mock" },
                        { cmd: "const { charge } = require('./services/paymentService');", comment: "" },
                        { cmd: "", comment: "" },
                        { cmd: "describe('Payment Service', () => {", comment: "" },
                        { cmd: "  it('processes a charge successfully', async () => {", comment: "" },
                        { cmd: "    const result = await charge(1000, 'tok_visa');", comment: "" },
                        { cmd: "    expect(result.status).toBe('succeeded');", comment: "Verify simulated output" },
                        { cmd: "  });", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                {/* 4. BONUS: EMAIL MOCKING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<EmailIcon />} label="Bonus: Mocking Email Services" />
                    <CodeBlock lines={[
                        { cmd: "jest.mock('@sendgrid/mail', () => ({", comment: "Inline mock pattern" },
                        { cmd: "  setApiKey: jest.fn(),", comment: "" },
                        { cmd: "  send: jest.fn().mockResolvedValue([{ statusCode: 202 }])", comment: "" },
                        { cmd: "}));", comment: "" },
                    ]} />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LightbulbIcon />} label="ProDev Best Practices" />
                    <Grid container spacing={2}>
                        {[
                            { title: "Isolation", desc: "Never hit real production APIs during tests. Use sandbox keys or mocks.", icon: <CheckCircleOutlineIcon /> },
                            { title: "Behavior Testing", desc: "Use .toHaveBeenCalledWith() to ensure your app sends the right data to the API.", icon: <CodeIcon /> },
                            { title: "Error Simulation", desc: "Use .mockRejectedValue() to test how your app handles API downtime.", icon: <BugReportIcon /> },
                        ].map((item) => (
                            <Grid item xs={12} md={4} key={item.title}>
                                <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        {React.cloneElement(item.icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                                        <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14 }}>{item.title}</Typography>
                                    </Box>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "999px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Place your mocks inside a <strong>__mocks__</strong> folder adjacent to the node_modules folder for automatic loading, or use <strong>jest.mock()</strong> at the top of individual test files for more granular control.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
